import { ACTOR_ID_HEADER_KEY, PAYMENT_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const METHODS_BASE = `${PAYMENT_SERVICE_BASE_URL}/api/v1/payments`;
const CORRELATION_ID_HEADER_KEY = "O-CORRELATION-ID";
const SKIP_WORKSPACE_CONTEXT_HEADER = "O-SKIP-WORKSPACE-CONTEXT";
const WORKSPACE_ID_HEADER = "O-WORKSPACE-ID";
const ORGANIZATION_OWNER_PARAM = `business${"Entity"}Id`;

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const actorHeaders = (actorId) =>
  actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};

const incomingWorkspaceHeaders = ({ actorId, workspaceId } = {}) => ({
  ...actorHeaders(actorId),
  ...(workspaceId
    ? {
        [SKIP_WORKSPACE_CONTEXT_HEADER]: "true",
        [WORKSPACE_ID_HEADER]: workspaceId,
      }
    : {}),
});

const ownerParams = ({ actorId, organizationId, userId, workspaceId, ...params } = {}) => ({
  ...params,
  ...(workspaceId ? { workspaceId } : {}),
  ...(organizationId
    ? { [ORGANIZATION_OWNER_PARAM]: organizationId }
    : { userId: userId || actorId || undefined }),
});

const correlationId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const value = (Math.random() * 16) | 0;
    return (char === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
};

export const paymentMethodsService = {
  outgoing: {
    list: async ({ actorId, organizationId, page = 0, size = 50, userId, workspaceId } = {}) => {
      const response = await apiClient.get(`${METHODS_BASE}/methods`, {
        params: ownerParams({
          actorId,
          organizationId,
          page,
          size,
          userId,
          workspaceId,
        }),
        headers: actorHeaders(actorId),
      });
      return pageContent(response.data);
    },

    get: async ({ actorId, methodId } = {}) => {
      const response = await apiClient.get(`${METHODS_BASE}/method`, {
        params: {
          pmid: methodId,
        },
        headers: actorHeaders(actorId),
      });
      return response.data || null;
    },

    setDefault: async ({ actorId, organizationId, methodId, userId, workspaceId } = {}) => {
      const response = await apiClient.put(
        `${METHODS_BASE}/method/${methodId}/set-default-outgoing`,
        undefined,
        {
          params: ownerParams({ actorId, organizationId, userId, workspaceId }),
          headers: actorHeaders(actorId),
        },
      );
      return response.data || {};
    },

    attach: async ({
      actorId,
      billingAddressLine1,
      billingCity,
      billingCountry,
      billingCounty,
      billingPostalCode,
      billingState,
      organizationId,
      paymentMethodId,
      userId,
      workspaceId,
    } = {}) => {
      const response = await apiClient.post(
        `${METHODS_BASE}/attach-method`,
        {
          paymentMethodId,
          billingAddressLine1: billingAddressLine1 || undefined,
          billingCity: billingCity || undefined,
          billingCountry: billingCountry || undefined,
          billingCounty: billingCounty || undefined,
          billingPostalCode: billingPostalCode || undefined,
          billingState: billingState || undefined,
          ...(workspaceId ? { workspaceId } : {}),
        },
        {
          params: ownerParams({ actorId, organizationId, userId, workspaceId }),
          headers: {
            ...actorHeaders(actorId),
            [CORRELATION_ID_HEADER_KEY]: correlationId(),
          },
        },
      );
      return response.data || {};
    },
  },

  incoming: {
    list: async ({ actorId, userId, workspaceId } = {}) => {
      const response = await apiClient.get(`${METHODS_BASE}/external/accounts/active`, {
        params: {
          uid: userId || actorId || undefined,
          workspaceId: workspaceId || undefined,
          wid: workspaceId || undefined,
        },
        headers: incomingWorkspaceHeaders({ actorId, workspaceId }),
      });
      return Array.isArray(response.data) ? response.data : [];
    },

    getConnectAccount: async ({ actorId, userId, workspaceId, refresh = false } = {}) => {
      const response = await apiClient.get(`${METHODS_BASE}/connect-account`, {
        params: {
          uid: userId || actorId || undefined,
          userId: actorId || undefined,
          refresh,
          workspaceId: workspaceId || undefined,
          wid: workspaceId || undefined,
        },
        headers: incomingWorkspaceHeaders({ actorId, workspaceId }),
      });
      return response.data || null;
    },

    createOnboardingLink: async ({ actorId, userId, workspaceId } = {}) => {
      const response = await apiClient.post(
        `${METHODS_BASE}/stripe-connect/helper/${encodeURIComponent(userId || actorId)}/onboard`,
        null,
        {
          params: {
            workspaceId: workspaceId || undefined,
            wid: workspaceId || undefined,
            userId: actorId || undefined,
          },
          headers: incomingWorkspaceHeaders({ actorId, workspaceId }),
        },
      );
      return response.data || {};
    },

    createDashboardLink: async ({ actorId, userId, workspaceId } = {}) => {
      const response = await apiClient.post(
        `${METHODS_BASE}/stripe-connect/helper/${encodeURIComponent(userId || actorId)}/dashboard`,
        null,
        {
          params: {
            workspaceId: workspaceId || undefined,
            wid: workspaceId || undefined,
            userId: actorId || undefined,
          },
          headers: incomingWorkspaceHeaders({ actorId, workspaceId }),
        },
      );
      return response.data || {};
    },
  },
};

export default paymentMethodsService;
